import { Heart, Gift, Users, ArrowRight, CheckCircle, Package, HandHeart, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function GetInvolved() {
  const waysToHelp = [
    {
      title: "Volunteer Your Time",
      description: "Join our volunteer team and make a direct impact in the community through hands-on service.",
      icon: Users,
      features: [
        "Distribution events and community outreach",
        "Supply organization and sorting",
        "Administrative and planning support",
        "Flexible scheduling to fit your availability"
      ],
      cta: "Become a Volunteer",
      link: "/volunteer"
    },
    {
      title: "Material Donations", 
      description: "Donate physical items that directly help those in need. We accept new and gently used items.",
      icon: Gift,
      features: [
        "New or gently used blankets and winter clothing",
        "Non-perishable food items and care packages",
        "Personal hygiene and sanitary supplies",
        "Baby care supplies for families with infants"
      ],
      cta: "Learn About Item Donations",
      link: "/contact"
    },
    {
      title: "Spread Awareness",
      description: "Help us reach more people in need by sharing our mission with your network and community.",
      icon: Heart,
      features: [
        "Share our story on social media platforms",
        "Tell friends and family about our mission",
        "Connect us with other community organizations",
        "Help identify areas of need in the community"
      ],
      cta: "Contact Us",
      link: "/contact"
    }
  ];

  const currentNeeds = [
    { item: "Warm Blankets", priority: "High", description: "New or gently used blankets for winter distribution" },
    { item: "Non-Perishable Food", priority: "High", description: "Canned goods, pasta, rice, and shelf-stable items" },
    { item: "Hygiene Supplies", priority: "Medium", description: "Soap, shampoo, toothbrushes, feminine hygiene products" },
    { item: "Winter Clothing", priority: "Medium", description: "Coats, hats, gloves, and warm socks" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Heart className="h-4 w-4 mr-2" />
              Ways to Help
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Involved with Our Mission
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              There are many meaningful ways to support The Blanket Project and help us bring 
              warmth, hope, and dignity to those who need it most in our community.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Help Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Three Ways to Make a Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the way that best fits your skills, availability, and passion for serving others
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {waysToHelp.map((way, index) => {
              const IconComponent = way.icon;
              return (
                <Card key={index} className="border border-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white h-full">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="h-8 w-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{way.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6 text-center flex-grow">{way.description}</p>
                    
                    <div className="mb-6">
                      <ul className="space-y-2">
                        {way.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Button asChild className="w-full bg-orange-600 hover:bg-orange-700 text-white mt-auto">
                      <Link to={way.link}>
                        {way.cta}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Current Needs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Current Priority Needs
            </h2>
            <p className="text-lg text-gray-600">
              These items are in highest demand for our upcoming community outreach events
            </p>
          </div>
          
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {currentNeeds.map((need, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{need.item}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        need.priority === 'High' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {need.priority} Priority
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{need.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">
                  Ready to donate items? Contact us to coordinate drop-off times and locations.
                </p>
                <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white">
                  <Link to="/contact">
                    <Package className="mr-2 h-4 w-4" />
                    Coordinate Item Donation
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Impact Through Service */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Impact Through Service
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every contribution, whether time or materials, creates real change in our community
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <HandHeart className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Direct Impact</h3>
              <p className="text-gray-600 text-sm">Your time and donations go directly to those in need, creating immediate positive change in their lives.</p>
            </div>
            <div>
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Community Building</h3>
              <p className="text-gray-600 text-sm">Join a community of like-minded individuals committed to serving others with compassion and faith.</p>
            </div>
            <div>
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Flexible Commitment</h3>
              <p className="text-gray-600 text-sm">Whether you have an hour or a day, there are meaningful ways to contribute that fit your schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Join us in living out Matthew 25:35-36 through practical acts of service and love. 
            Every person who gets involved helps us expand our reach and impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              <Link to="/volunteer">
                Start Volunteering
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              asChild 
              className="border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-3 rounded-full text-lg font-semibold transition-all duration-300"
            >
              <Link to="/contact">Ask Questions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}